import { Image as ImageIcon, Loader2, Sparkles, Trash2, Upload } from 'lucide-react';

interface RoomLayout {
  id: string;
  name: string;
  description: string;
  imageUrl: string;
  minCapacity: number | null;
  maxCapacity: number | null;
  order: number;
  isUploading?: boolean;
  pendingFile?: File;
  previewUrl?: string;
}

interface LayoutCardProps {
  layout: RoomLayout;
  onUpdate: (id: string, updates: Partial<RoomLayout>) => void;
  onDelete: (id: string) => void;
  onImageUpload: (id: string, event: React.ChangeEvent<HTMLInputElement>) => void;
}

export function LayoutCard({ layout, onUpdate, onDelete, onImageUpload }: LayoutCardProps) {
  return (
    <div
      style={{
        padding: '1rem',
        background: 'white',
        border: '2px solid #e5e7eb',
        borderRadius: '12px',
        transition: 'all 0.2s ease',
      }}
    >
      <div style={{ marginBottom: '1rem' }}>
        {layout.previewUrl || layout.imageUrl ? (
          <div style={{ position: 'relative' }}>
            <img
              src={layout.previewUrl || layout.imageUrl}
              alt={layout.name}
              style={{
                width: '100%',
                height: '180px',
                objectFit: 'cover',
                borderRadius: '8px',
                opacity: layout.isUploading ? 0.5 : 1,
              }}
            />
            {layout.isUploading && (
              <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }}>
                <Loader2 size={32} color="#00758d" style={{ animation: 'spin 1s linear infinite' }} />
              </div>
            )}
            {!layout.isUploading && (
              <label
                style={{
                  position: 'absolute',
                  bottom: '0.5rem',
                  right: '0.5rem',
                  padding: '0.5rem',
                  background: 'rgba(0,0,0,0.7)',
                  color: 'white',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.25rem',
                  fontSize: '0.8rem',
                }}
              >
                <Upload size={14} />
                Changer
                <input type="file" accept="image/*" onChange={(e) => onImageUpload(layout.id, e)} style={{ display: 'none' }} />
              </label>
            )}
          </div>
        ) : (
          <label
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              height: '180px',
              background: '#f8fafb',
              border: '2px dashed #cbd5e1',
              borderRadius: '8px',
              cursor: layout.isUploading ? 'not-allowed' : 'pointer',
              transition: 'all 0.2s ease',
              opacity: layout.isUploading ? 0.5 : 1,
            }}
            onMouseEnter={(e) => {
              if (!layout.isUploading) {
                e.currentTarget.style.borderColor = '#00758d';
                e.currentTarget.style.background = 'rgba(0,117,141,0.05)';
              }
            }}
            onMouseLeave={(e) => {
              if (!layout.isUploading) {
                e.currentTarget.style.borderColor = '#cbd5e1';
                e.currentTarget.style.background = '#f8fafb';
              }
            }}
          >
            {layout.isUploading ? (
              <>
                <Loader2 size={32} color="#00758d" style={{ animation: 'spin 1s linear infinite' }} />
                <span style={{ marginTop: '0.5rem', fontSize: '0.85rem', color: '#00758d', fontWeight: 600 }}>Upload en cours...</span>
              </>
            ) : layout.name.toLowerCase().includes('autre') ? (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
                <div style={{ width: '48px', height: '48px', borderRadius: '50%', background: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 4px rgba(0,0,0,0.05)' }}>
                  <Sparkles size={24} color="#00758d" />
                </div>
                <span style={{ fontSize: '0.85rem', color: '#6b7280', textAlign: 'center' }}>L'icône sera affichée<br />(ou cliquez pour photo)</span>
              </div>
            ) : (
              <>
                <ImageIcon size={32} color="#cbd5e1" />
                <span style={{ marginTop: '0.5rem', fontSize: '0.85rem', color: '#6b7280' }}>Cliquez pour ajouter une photo</span>
              </>
            )}
            <input type="file" accept="image/*" onChange={(e) => onImageUpload(layout.id, e)} style={{ display: 'none' }} disabled={layout.isUploading} />
          </label>
        )}
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
        <input
          type="text"
          value={layout.name}
          onChange={(e) => onUpdate(layout.id, { name: e.target.value })}
          placeholder="Nom de la mise en place *"
          style={{ padding: '0.75rem', fontSize: '0.95rem', fontWeight: 600, border: '1px solid #e5e7eb', borderRadius: '8px', outline: 'none' }}
        />
        <textarea
          value={layout.description}
          onChange={(e) => onUpdate(layout.id, { description: e.target.value })}
          placeholder="Description"
          rows={2}
          style={{ padding: '0.75rem', fontSize: '0.85rem', border: '1px solid #e5e7eb', borderRadius: '8px', outline: 'none', resize: 'vertical', fontFamily: 'inherit' }}
        />
        <input
          type="number"
          value={layout.minCapacity || ''}
          onChange={(e) => onUpdate(layout.id, { minCapacity: e.target.value ? parseInt(e.target.value) : null })}
          placeholder="Capacité minimale (optionnel)"
          style={{ padding: '0.75rem', fontSize: '0.85rem', border: '1px solid #e5e7eb', borderRadius: '8px', outline: 'none' }}
        />
        <input
          type="number"
          value={layout.maxCapacity || ''}
          onChange={(e) => onUpdate(layout.id, { maxCapacity: e.target.value ? parseInt(e.target.value) : null })}
          placeholder="Capacité maximale (optionnel)"
          style={{ padding: '0.75rem', fontSize: '0.85rem', border: '1px solid #e5e7eb', borderRadius: '8px', outline: 'none' }}
        />
      </div>

      <button
        type="button"
        onClick={() => onDelete(layout.id)}
        style={{ width: '100%', marginTop: '0.75rem', padding: '0.5rem', background: '#f4d9e5', color: '#8E0B56', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '0.85rem', fontWeight: 600, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', transition: 'all 0.2s ease' }}
        onMouseEnter={(e) => (e.currentTarget.style.background = '#e8bccf')}
        onMouseLeave={(e) => (e.currentTarget.style.background = '#f4d9e5')}
      >
        <Trash2 size={16} /> Supprimer
      </button>
    </div>
  );
}
